import Page from "./Page"
import Slice from "./Slice"
import SequenceSlice from "./SequenceSlice"
import TransitionManager from "./TransitionManager"

import * as constants from "./constants"
import * as Utils from "./utils"

export default class Story {

	constructor(player, textManager, interactionManager, resourceManager,
		contentContainer, setRatioConstraint) {
		this._player = player
		this._textManager = textManager
		this._interactionManager = interactionManager
		this._resourceManager = resourceManager
		this._contentContainer = contentContainer
		this._setRatioConstraint = setRatioConstraint

		// _singlePagesArray = _pagesArray as long as double pages are not defined
		// (i.e. before we implement the spread functionality)
		this._singlePagesArray = []
		this._pagesArray = []
		this._pageIndex = null
		this._transitionManager = null
	}

	loadFromPath(folderPath) {
		this._folderPath = folderPath
		this._loadJson()
			.then((json) => {
				this._buildStoryFromJson(json)
			}, (error) => this._textManager.showMessage({
				type: "error", text: error.message,
			}))
	}

	loadFromData(data) {
		this._data = data
		this._buildStoryFromJson(data.json)
	}

	_loadJson() {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest()
			xhr.open("GET", `${this._folderPath}/${constants.defaultManifestFilename}`)
			xhr.responseType = "text"
			xhr.onload = () => {
				const text = xhr.response
				try {
					const json = JSON.parse(text)
					resolve(json)
				} catch (error) {
					reject(error)
				}
			}
			xhr.onerror = (error) => {
				reject(error)
			}
			xhr.send()
		})
	}

	_buildStoryFromJson(json) {
		const { metadata, readingOrder } = json
		if (!metadata || !readingOrder) {
			this._textManager.showMessage({
				type: "error", text: "Missing metadata or readingOrder information",
			})
			return
		}
		const { readingProgression, presentation } = metadata
		if (!readingProgression || !presentation) {
			this._textManager.showMessage({
				type: "error", text: "Missing readingProgression or presentation information",
			})
			return
		}
		this._readingProgression = readingProgression

		const {
			viewportRatio,
			continuous,
			overflow,
			fit,
			snapBehavior,
		} = presentation

		// Build pages based on readingOrder data
		const realContinuous = continuous || constants.defaultContinuous
		const storyOverflow = overflow || constants.defaultOverflow
		const storyFit = fit || constants.defaultFit
		this._buildPages(readingOrder, realContinuous, storyOverflow, storyFit)

		this._interactionManager.adaptToStory(this._readingProgression, snapBehavior,
			this._goForward.bind(this), this._goBackward.bind(this),
			this._goToClosestSnapPoint.bind(this), this._setScrollDelta.bind(this))

		// Load all resources in the PIXI loader
		this._resourceManager.loadAllResources()
			.then(() => {
				// Remove the textManager
				this._textManager.destroy()
				this._textManager = null

				// Now that resources have been loaded, PIXI textures can be created
				this._singlePagesArray.forEach((page) => {
					page.finishBuildingSlices()
				})
				if (this._transitionManager) {
					this._transitionManager.finishBuildingSlices()
				}
				// Update HTML canvas size to conform to viewportRatio constraints
				// (will trigger a this.resize() that also computes snap point speeds via Page)
				this._setRatioConstraint(viewportRatio)

				this._pagesArray = this._singlePagesArray
				this._goToPageWithIndex(0)

			}, (error) => this._textManager.showMessage({
				type: "error", text: error.message,
			}))
	}

	_buildPages(readingOrder, continuous, storyOverflow, storyFit) {
		let currentPage = null
		readingOrder.forEach((linkObject) => {
			const {
				href, type, fallbackHref, properties,
			} = linkObject
			const {
				overflow,
				fit,
				transitionForward,
				transitionBackward,
				snapPoints,
			} = properties || {}

			const sliceOverflow = overflow || storyOverflow
			const sliceFit = fit || storyFit

			// It will be time to create a new page if...
			if (!currentPage // ... this is the first page...
				|| continuous === false // ... or the story is "paginated"
				|| transitionForward) { // ... or the story is a "paginated webtoon"

				// Which means that, if a page was being created, store it in the list first...
				if (currentPage) {
					this._singlePagesArray.push(currentPage)
				}
				// ...and then only start creating the new one
				currentPage = new Page(this._player, this._readingProgression)

				// If the linkObject has transitions, add them to the page being created
				if (transitionForward) {
					currentPage.setTransition("forward", this._createTransition(transitionForward,
						sliceOverflow, sliceFit))
				}
				if (transitionBackward) {
					currentPage.setTransition("backward", this._createTransition(transitionBackward,
						sliceOverflow, sliceFit))
				}
			}

			// Now create the slice corresponding to the linkObject's resource, and add it to the page
			// Do note that, at this stage, only a webtoon page can have several slices
			const resourceData = Utils.getResourceData(this._folderPath, href, type, fallbackHref)

			// If the DiViNa was opened with data (i.e. not from a folder) and the resource is a video,
			// directly copy its dataURI in the resource path to allow video.src = path (in Slice)
			if (!this._folderPath && resourceData.resourceType === "video") {
				resourceData.path = this._data.base64DataByHref[href]
			}

			const slice = new Slice(resourceData, sliceOverflow, sliceFit, this._player)
			currentPage.addSlice(slice)

			// Add the resource to the list of resources to be loaded by PIXI
			this._resourceManager.addResource(resourceData)

			// If snapPoints have been defined and are actually relevant
			if (snapPoints
				&& (storyOverflow === "scrolled" || sliceOverflow === "scrolled")) {
				currentPage.addSnapPoints(snapPoints)
			}
		})

		// Do not forget to add the last created page to the list
		this._singlePagesArray.push(currentPage)
	}

	_createTransition(transition, overflow, fit) {
		if (!transition) {
			return null
		}

		// Create a transitionManager from the moment there is at least one transition
		if (!this._transitionManager) {
			this._transitionManager = new TransitionManager(this._player, this._contentContainer)
		}

		const fullTransition = { ...transition }

		// If the transition is of the "sequence" type, build a sequence slice
		if (transition.type === "sequence" && transition.sequence) {
			const { sequence } = transition
			const resourceDataArray = []
			sequence.forEach((linkObject) => {
				const { href } = linkObject
				const resourceData = Utils.getResourceData(this._folderPath, href, "image")
				resourceDataArray.push(resourceData)
				this._resourceManager.addResource(resourceData)
			})
			const duration = transition.duration || constants.defaultDuration
			const sequenceSlice = new SequenceSlice(resourceDataArray,
				duration, overflow, fit, this._player)
			fullTransition.sequenceSlice = sequenceSlice
			this._transitionManager.addSequenceSlice(sequenceSlice)
		}

		// If the transition is of the "video" type, build a video slice
		if (transition.type === "video" && transition.href) {
			const { href } = transition
			const resourceData = Utils.getResourceData(this._folderPath, href, "video")

			// If the DiViNa was opened with data (i.e. not from a folder), set dataURI as path
			if (!this._folderPath && resourceData.resourceType === "video") {
				resourceData.path = this._data.base64DataByHref[href]
			}

			const videoSlice = new Slice(resourceData, overflow, fit, this._player)
			fullTransition.videoSlice = videoSlice
			this._transitionManager.addVideoSlice(videoSlice)
		}

		return fullTransition
	}

	// For navigating the story

	_goToPageWithIndex(index, sliceIndex) {
		if ((index === this._pageIndex && sliceIndex === undefined)
			|| index < 0 || index > this._pagesArray.length - 1) {
			return
		}

		const isGoingForward = (this._pageIndex === null || index - this._pageIndex >= 0)
		this._pageIndex = index

		const oldPage = this._currentPage
		this._currentPage = this._pagesArray[this._pageIndex]

		// Resize the new page in all cases before it gets displayed
		this._currentPage.resize()

		// Check if a transition needs to be run
		let transition = null
		if (oldPage && isGoingForward === true) {
			transition = oldPage.transitionForward
		}
		if (isGoingForward === false) {
			transition = this._currentPage.transitionBackward
		}

		// If there is no transition to run or if the function has been called by a goTo,
		// update the content of the contentContainer with that of the new page
		if (!transition || sliceIndex !== undefined) {
			this._contentContainer.removeChildren()
			this._currentPage.pushContentIntoContainer(this._contentContainer)

			// If page videos need to be played or stopped
			this._currentPage.play()
			if (oldPage) {
				oldPage.stop()
			}

			// If the transition has been called by a goTo, go to the relevant slice directly
			if (sliceIndex !== undefined) {
				this._currentPage.goToSliceIndex(sliceIndex, isGoingForward)
			}

		// If there is a transition to run, run it
		} else if (this._transitionManager) {
			this._transitionManager.run(transition, this._currentPage, oldPage, isGoingForward)
		}
	}

	_goForward() {
		const isGoingForward = true
		this._turnPageOrJumpWithinAfterForcingIfRelevant(isGoingForward)
	}

	_goBackward() {
		const isGoingForward = false
		this._turnPageOrJumpWithinAfterForcingIfRelevant(isGoingForward)
	}

	_turnPageOrJumpWithinAfterForcingIfRelevant(isGoingForward) {
		if (!this._currentPage) {
			return
		}
		const callback = (isGoingForward === true)
			? this._turnPageOrJumpWithinForward.bind(this)
			: this._turnPageOrJumpWithinBackward.bind(this)

		// If the page is jumping between snap points
		if (this._currentPage.isAutoScrolling === true) {

			// Force jump to end then run callback only if directions differ
			this._currentPage.forceJump(callback, isGoingForward)

		// Else if the page is being turned...
		} else if (this._transitionManager && this._transitionManager.isRunning === true) {

			// Force page change to end then run callback only if directions differ
			this._transitionManager.forceTransitionToEnd(callback, isGoingForward)

		// Otherwise just run the callback right away
		} else {
			callback()
		}
	}

	_turnPageOrJumpWithinForward() {
		if (!this._currentPage) {
			return
		}
		const { isAtEnd } = this._currentPage
		// If the end of the page has been reached, turn the page to the next one
		if (isAtEnd === true) {
			this._goToNextPage()
		// Otherwise just jump to the next snap point
		} else {
			this._goToNextSnapPoint()
		}
	}

	_goToNextPage() {
		this._goToPageWithIndex(this._pageIndex + 1)
	}

	_goToNextSnapPoint() {
		this._currentPage.goToNextSnapPoint()
	}

	_turnPageOrJumpWithinBackward() {
		if (!this._currentPage) {
			return
		}
		const { isAtStart } = this._currentPage
		// If the page is at its start, turn the page to the previous one
		if (isAtStart === true) {
			this._goToPreviousPage()
		// Otherwise just jump to the previous snap point
		} else {
			this._goToPreviousSnapPoint()
		}
	}

	_goToPreviousPage() {
		this._goToPageWithIndex(this._pageIndex - 1)
	}

	_goToPreviousSnapPoint() {
		if (!this._currentPage) {
			return
		}
		this._currentPage.goToPreviousSnapPoint()
	}

	_goToClosestSnapPoint() {
		if (!this._currentPage) {
			return
		}
		this._currentPage.goToClosestSnapPoint()
	}

	_setScrollDelta(scrollDelta) {
		// If the page is jumping between snap points or transitioning, do nothing
		if (!this._currentPage || this._currentPage.isAutoScrolling === true
			|| (this._transitionManager && this._transitionManager.isRunning === true)) {
			return
		}
		// Otherwise scroll the page
		this._currentPage.applyScrollDelta(scrollDelta)
	}

	// For reaching a specific point directly in the story (typically via a table of contents)
	goTo(href) {
		if (!this._pagesArray || this._pagesArray.length === 0) {
			return
		}
		// Try and find what page and slice indices the given href corresponds to
		let hasFoundPath = false
		let pageIndex = 0
		let sliceIndex = 0
		while (pageIndex < this._pagesArray.length && hasFoundPath === false) {
			const page = this._pagesArray[pageIndex]
			const path = Utils.getPathFromHref(this._folderPath, href)
			const targetSliceIndex = page.getSliceIndexInPageForPath(path)
			if (targetSliceIndex !== null) {
				hasFoundPath = true
				sliceIndex = targetSliceIndex
			}
			pageIndex += 1
		}

		// If the href has been found in the resource list, reach for it
		if (hasFoundPath === true) {
			this._goToPageWithIndex(pageIndex - 1, sliceIndex)
		// Otherwise just go to the beginning of the story
		} else {
			this._goToPageWithIndex(0)
		}
	}

	resize() {
		// Ensure that a running transition manager resizes its old and new pages
		// along with its current sequence or video slice, as required
		if (this._transitionManager && this._transitionManager.isRunning === true) {
			this._transitionManager.resize()
		// Otherwise just resize the current page
		} else if (this._currentPage) {
			this._currentPage.resize()
		}
	}

	destroy() {
		if (this._transitionManager) {
			this._transitionManager.destroy()
		}
		this._pagesArray.forEach((page) => {
			page.destroy()
		})
	}

}